"use client";

import { useIsRestoring, useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTenantData } from "@/hooks/shared/useTenantData";
import { getMediaAssetQueryKey } from "@/lib/utils/media/media-asset-query-key";
import {
  fetchMediaAsset,
  type MediaOwnerType,
} from "@/lib/utils/media/fetch-media-asset";
import {
  PUBLIC_PRESIGNED_URL_GC_MS,
  PUBLIC_PRESIGNED_URL_STALE_MS,
} from "@/lib/utils/media/public-media-cache";
import type { MediaGroup } from "@/openapi/generated/models";

function uniquePositiveIds(ownerIds: number[]): number[] {
  const seen = new Set<number>();
  const ids: number[] = [];
  for (const id of ownerIds) {
    if (id <= 0 || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

function usePublicListingMediaQueries({
  ownerType,
  ownerIds,
  mediaGroup,
}: {
  ownerType: MediaOwnerType;
  ownerIds: number[];
  mediaGroup: MediaGroup;
}) {
  const { tenantId } = useTenantData();
  const isRestoring = useIsRestoring();
  const uniqueIds = useMemo(() => uniquePositiveIds(ownerIds), [ownerIds]);

  const results = useQueries({
    queries: uniqueIds.map((ownerId) => ({
      queryKey: getMediaAssetQueryKey(
        tenantId,
        ownerType,
        ownerId,
        mediaGroup,
        "public",
      ),
      queryFn: () =>
        fetchMediaAsset({
          tenantId,
          ownerType,
          ownerId,
          mediaGroup,
        }).catch(() => ({
          url: null,
          mediaId: null,
          media: null,
        })),
      enabled: tenantId > 0 && ownerId > 0 && !isRestoring,
      staleTime: PUBLIC_PRESIGNED_URL_STALE_MS,
      gcTime: PUBLIC_PRESIGNED_URL_GC_MS,
      refetchOnMount: false,
      // Same as useMediaAssetGet: RQ only refetches when stale (~25m).
      refetchOnWindowFocus: true,
      retry: 1,
    })),
  });

  return { uniqueIds, results };
}

/**
 * Batch public media URLs for listing rows.
 * Uses the same query keys as `useMediaAssetGet` so detail pages reuse the cache.
 */
export function usePublicListingMediaUrls({
  ownerType,
  ownerIds,
  mediaGroup,
}: {
  ownerType: MediaOwnerType;
  ownerIds: number[];
  mediaGroup: MediaGroup;
}): Record<number, string | null> {
  const { uniqueIds, results } = usePublicListingMediaQueries({
    ownerType,
    ownerIds,
    mediaGroup,
  });

  return useMemo(() => {
    const map: Record<number, string | null> = {};
    uniqueIds.forEach((id, index) => {
      map[id] = results[index]?.data?.url ?? null;
    });
    return map;
  }, [uniqueIds, results]);
}

/** Warm the next listing page's media cache without blocking the current rows. */
export function usePrefetchPublicListingMediaUrls({
  ownerType,
  ownerIds,
  mediaGroup,
}: {
  ownerType: MediaOwnerType;
  ownerIds: number[];
  mediaGroup: MediaGroup;
}): void {
  usePublicListingMediaQueries({ ownerType, ownerIds, mediaGroup });
}
